<template>
  <div class="main-content">
    <!-- Page Header Start -->
    <PageHeader>
      <PageHeaderTitleContent>
        <TitleTextContent
          :titleText="templateTitle"
        />
        <div style="min-width: 430px;" class="d-flex justify-content-center mt-3">
          <div class="w-100 d-flex flex-column flex-lg-row flex-xl-row align-items-center justify-content-lg-between justify-content-xl-between">
            <router-link
              :to="{
                name: 'client-templates'
              }"
            >
              <v-btn
                rounded
                large
                dark
                :color="primaryButtonColor"
                class="mb-4 mb-lg-0 mb-xl-0"
              >Back To All Templates</v-btn>
            </router-link>

            <v-btn
              rounded
              large
              dark
              :color="primaryButtonColor"
              @click="downloadHandler"
            >Download Template</v-btn>
          </div>
        </div>
      </PageHeaderTitleContent>
    </PageHeader>
    <!-- Page Header End -->

    <!-- Template PDF And Description Start  -->
    <section class="section p-0 mt-5 mb-5 section-content">
      <div class="container p-0">
        <div class="row m-0">
          <div class="col-lg-4 col-xl-4 col-12 p-0">
            <div class="template-container">
              <div class="pdf-container pt-2 px-2">
                <div v-if="isPdfAvailable" class="pdf-template">
                  <embed :src="`${templatePdfUrl}#toolbar=0&navpanes=0&scrollbar=0`" width="100%" height="100%" style="padding-bottom: '56.25%';"/>
                </div>
                <div v-else class="pdf-failed">
                  The PDF file for "{{ templateTitle }}" failed to be displayed and is not available.
                </div>
              </div>
            </div>
          </div>
          <div class="col-lg-8 col-xl-8 col-12 p-0 pb-3 px-3 mt-4 mt-lg-0 mt-xl-0">
            <div>
              <p class="pdf-description pdf-template-description-title">About this template</p>
            </div>
            <div>
              <p class="pdf-template-description-text" v-html="templateDescription"></p>
            </div>
          </div>
        </div>
      </div>
    </section>
    <!-- Template PDF And Description End -->

    <!-- Modal Download Status Start -->
    <ModalWidget
      id="modal-download-status"
      title="Download Document Status"
      centered
      hideFooter
      :hideHeaderClose="!isDownloadCompleted"
      :noCloseOnBackdrop="!isDownloadCompleted"
      :closeHandler="closeModal"
    >
      <p 
        slot="body" 
        class="modal-paragraph text-center px-5"
        :style="{ color: modalTextColor }"
      >
        {{ modalStatusStatement }}
        &nbsp;
        <font-awesome-icon
          v-if="currentDownloadStatus?.status === 'Success'"
          size="lg"
          :icon="['fas', 'check-circle']" 
        />
        <font-awesome-icon
          v-if="currentDownloadStatus?.status === 'Failed'"
          size="lg"
          :icon="['fas', 'times-circle']"
        />
      </p>
      <b-button
        slot="row-buttons"
        id="input-3"
        key=""
        class="modal-btn"
        size="sm"
        :disabled="!isDownloadCompleted"
        @click="closeModal"
        >OK
      </b-button>
    </ModalWidget>
    <!-- Modal Download Status End -->
  </div>
</template>

<script>
  import PageHeader from '../library/widgets/page-header.vue';
  import PageHeaderTitleContent from '../library/widgets/page-header-title-content.vue';
  import TitleTextContent from '../library/widgets/title-text-content.vue';
  import ModalWidget from '../library/widgets/modal-widget.vue';
  import clientTemplatesData from '../../utils/clientTemplatesData';

  export default {
    name: 'client-Template-item',
    props: {
      templateId: String,
    },
    data(){
      return {
        templateTitle: null,
        templateDescription: null,
        templatePdfUrl: null,
        templateDocUrl: null,
        templateDownloadFilename: null,
        currentDownloadStatus: null,
        isPdfAvailable: false,
        downloading_status: {
          inProgress: {
            styleColor: 'yellow',
            statusStatement: `<document_filename> download is now in progress...`,
            status: 'In Progress',
          },
          successful: {
            styleColor: 'green',
            statusStatement: `<document_filename> has successfully been downloaded in your downloads folder.`,
            status: 'Success',
          },
          failure: {
            styleColor: 'red',
            statusStatement: `<document_filename> has failed to download.`,
            status: 'Failed',
          },
        },
      };
    },
    components: {
      PageHeader,
      PageHeaderTitleContent,
      TitleTextContent,
      ModalWidget,
    },
    computed: {
      isDownloadCompleted() {
        return this.currentDownloadStatus?.status === 'Success' || this.currentDownloadStatus?.status === 'Failed';
      },
      modalStatusStatement() {
        return this.currentDownloadStatus?.statusStatement?.replace('<document_filename>', this.templateDownloadFilename);
      },
      modalTextColor() {
        return this.currentDownloadStatus?.styleColor;
      }
    },
    created() {
      const { title, description, pdfUrl, docUrl, downloadFilename } = clientTemplatesData.find(({ id }) => id === this.templateId);
      this.templateTitle = title;
      this.templateDescription = description;
      this.templatePdfUrl = pdfUrl;
      this.templateDocUrl = docUrl;
      this.templateDownloadFilename = downloadFilename;
    },
    mounted() {
      this.pdfDisplayHandler();
    },
    methods: {
      closeModal() {
        this.$bvModal.hide('modal-download-status');
        this.currentDownloadStatus = null;
      },
      async pdfDisplayHandler() {
        try {
          const response = await fetch(this.templatePdfUrl);
          if(!response.ok) throw new Error(`Unable to receive pdf: ${this.templatePdfUrl}`);
          this.isPdfAvailable = true;
        }
        catch(error) {
          this.isPdfAvailable = false;
          console.error(error.message);
        }
      },
      async downloadHandler() {
        this.currentDownloadStatus = this.downloading_status['inProgress'];
        this.$bvModal.show('modal-download-status');

        try {
          await this.downloadUrl(this.templateDocUrl, this.templateDownloadFilename);
          this.currentDownloadStatus = this.downloading_status['successful'];
        }
        catch(error) {
          this.currentDownloadStatus = this.downloading_status['failure'];
          console.error(error.message);
        }
      },
    }
  }
</script>

<style scoped>
  .template-container {
    width: 100%;
    height: 470px;
    border: 2px solid #272727;
  }

  .pdf-container {
    width: 100%;
    height: 100%;
    overflow: auto;
  }

  p.pdf-description {
    color: #272727;
    font-size: 25px;
    font-style: normal;
    font-weight: 400;
  }

  .pdf-template {
    width: 100%;
    height: 100%;
    overflow-y: hidden;
  }

  .pdf-template-description-title {
    font-family: GeomGraphic;
    line-height: normal;
  }

  .pdf-template-description-text {
    color: #272727;
    font-family: Rajdhani;
    font-size: 25px;
    font-style: normal;
    font-weight: 400;
    line-height: 35px;
  }

  .pdf-failed {
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    font-weight: 500;
    font-size: 24px;
    height: 100%;
  }

  .modal-paragraph {
    color: #272727;
    font-family: Rajdhani;
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px;
    margin: 0;
    margin-bottom: 5px;
  }

  .modal-btn {
    width: 220px;
    height: 44px;
    padding: 10px 0px;
    font-family: Roboto;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
    border-radius: 50px;
    background-color: #424EC4;
  }
</style>